import * as THREE from 'three';
import { damp } from '@/math/angles';
import { PartBuilder, capeTexture, clothMesh, faceTexture, goalNetTexture, hairTexture, scarfTexture, type ClothHandle, type ModColors, type ModMaterials } from './modKit';
import type { ModCtx, PartFn } from './modTypes';

/**
 * partsSoft.ts (M128) — the mods that MOVE like something alive: the
 * ponytail (a verlet chain driving a skinned hair tube), the fan tifo (a
 * vertex-shader banner on a bending pole) and the cheering fans (small
 * merged figures with one arm pivot each and a scarf cloth).
 */

const WHITE = 0xf1f1ee;
const DARK = 0x15161a;
const HAIR = 0x2a1a0e;
const RUBBER = 0x24252a;
const SKINS = [0xd9a181, 0x9a6645, 0x573826, 0xedc9a8];
const HAIRS = [0x17120e, 0x6b4720, 0xd8b866, 0xc2331f];
const R = (deg: number): number => (deg * Math.PI) / 180;
const clamp01 = (x: number): number => Math.min(1, Math.max(0, x));
const windOf = (ctx: ModCtx): number => clamp01(ctx.speed / 35);
const V = (x: number, y: number, z: number): THREE.Vector3 => new THREE.Vector3(x, y, z);
const Z = V(0, 0, 1);


import { CAPE_FOLD, CHASE_ELEVATION, capeShape, type CapeSpec } from './capeShape';
import type { ModAnchors } from './modAnchors';
import { CREW_ROW, fanLayout, pillionSeat, restTopAt, roofHalfAt, type FanMode } from './modProfile';
import { goalwingCovers, goalwingFrame, mudflapLayout, roofNumberZ, whistleBounds, whistleCrossTops } from './partsHard';
import type { SurfaceField } from './modSurface';
export { bodyTopAt, pillionSeat, restTopAt } from './modProfile';

// ---------------------------------------------------------------------------
// Ponytail — a scrunchie at the roof's rear centre (the rider's helmet on the
// bike and the Formula), a skinned hair tube on a nine-link chain simulated
// with gravity, wind and the car's own accelerations: hangs at the grid,
// lifts as you go, streams flat at speed, whips outward in corners.
// ---------------------------------------------------------------------------
export const ponytail: PartFn = (a, c, m, opts) => {
  const s = a.s;
  const group = new THREE.Group();
  // Cars: the roof's rear centre. Bike: the helmet's back. Formula: the
  // helmet's crown, so the hair lies over the airbox behind it. M153: the
  // open muscle car's hair grows from its DRIVER's helmet and falls over the
  // seat back onto the deck (the roof-rear point is its trunk lid — the hair
  // grew out of the bodywork, attached to no one).
  const root =
    a.openTop && a.helmetBack
      ? a.helmetBack.clone().add(V(0, 0.07 * s, 0))
      : a.kind === 'car'
      ? a.roofRear.clone().add(V(0, 0.12 * s, 0.1 * s))
      : a.kind === 'f1'
        ? (a.helmetBack ?? a.roofRear).clone().add(V(0, 0.2 * s, 0))
        : (a.helmetBack ?? a.roofRear).clone();
  const pb = new PartBuilder();
  pb.torus(0.1 * s, 0.05 * s, root.x, root.y, root.z, c.primary, { bucket: 'paint' });
  pb.sphere(0.135 * s, root.x, root.y, root.z + 0.05 * s, HAIR, { scale: [1, 1, 0.75] });
  group.add(pb.build(m));

  const N = 9;
  const seg = 0.15 * s;
  const rest = V(0, -0.28, -0.96).normalize();
  const p0 = root.clone().add(V(0, 0, -0.07 * s));
  const pts: THREE.Vector3[] = [];
  const prev: THREE.Vector3[] = [];
  for (let i = 0; i < N; i++) {
    pts.push(p0.clone().addScaledVector(rest, seg * i));
    prev.push(pts[i]!.clone());
  }
  const tail = new THREE.Group();
  group.add(tail);
  const bones: THREE.Bone[] = [];
  for (let i = 0; i < N; i++) {
    const b = new THREE.Bone();
    b.position.copy(pts[i]!);
    b.quaternion.setFromUnitVectors(Z, rest);
    tail.add(b);
    bones.push(b);
  }
  // The hair: THREE strand bundles twisting round the chain (one thick tube
  // read as a cone, measured), each a ring per bone with a bumpy cross-
  // section, tapering to alpha-cut wispy tips; every ring bound 100% to its
  // bone so the verlet chain carries all three.
  const SEGS = 12;
  const rings = N + 1;
  const pos: number[] = [];
  const nrm: number[] = [];
  const uv: number[] = [];
  const skinI: number[] = [];
  const skinW: number[] = [];
  const idx: number[] = [];
  const radiusAt = (f: number): number => (0.15 * Math.pow(1 - f, 0.5) + 0.02) * s;
  const strands: { scale: number; off: number; phase: number; twist: number; u0: number }[] = [
    { scale: 0.78, off: 0.0, phase: 0, twist: 0, u0: 0 },
    { scale: 0.56, off: 0.62, phase: 0.4, twist: 1.1, u0: 0.37 },
    { scale: 0.52, off: 0.66, phase: 0.4 + (Math.PI * 2) / 3, twist: -0.9, u0: 0.71 },
  ];
  for (const st of strands) {
    const base = pos.length / 3;
    for (let i = 0; i < rings; i++) {
      const bi = Math.min(i, N - 1);
      const f = i / N;
      const centre = i < N ? pts[i]! : pts[N - 1]!.clone().addScaledVector(rest, seg * 0.9);
      const q = bones[bi]!.quaternion;
      const r0 = radiusAt(Math.min(f, 1));
      const r = i < N ? r0 * st.scale : 0.006 * s;
      const ang = st.phase + st.twist * f * Math.PI * 2;
      const ox = Math.cos(ang) * r0 * st.off;
      const oy = Math.sin(ang) * r0 * st.off;
      for (let j = 0; j <= SEGS; j++) {
        const th = (j / SEGS) * Math.PI * 2;
        const bump = 1 + 0.18 * Math.sin(5 * th + i * 1.1 + st.phase);
        const o = V(Math.cos(th) * r * bump + ox, Math.sin(th) * r * bump + oy, 0).applyQuaternion(q);
        pos.push(centre.x + o.x, centre.y + o.y, centre.z + o.z);
        const n = V(Math.cos(th), Math.sin(th), 0).applyQuaternion(q);
        nrm.push(n.x, n.y, n.z);
        uv.push(st.u0 + (j / SEGS) * 1.5, Math.min(1, f));
        skinI.push(bi, 0, 0, 0);
        skinW.push(1, 0, 0, 0);
      }
    }
    for (let i = 0; i < rings - 1; i++) {
      for (let j = 0; j < SEGS; j++) {
        const A = base + i * (SEGS + 1) + j;
        const B = A + SEGS + 1;
        idx.push(A, B, A + 1, A + 1, B, B + 1);
      }
    }
  }
  const geo = new THREE.BufferGeometry();
  geo.setAttribute('position', new THREE.Float32BufferAttribute(pos, 3));
  geo.setAttribute('normal', new THREE.Float32BufferAttribute(nrm, 3));
  geo.setAttribute('uv', new THREE.Float32BufferAttribute(uv, 2));
  geo.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(skinI, 4));
  geo.setAttribute('skinWeight', new THREE.Float32BufferAttribute(skinW, 4));
  geo.setIndex(idx);
  // Matte on purpose: a semi-gloss dark tube under the sky IBL rendered as a
  // grey-blue sheen from the chase camera (measured). alphaTest cuts the tips.
  const mat = m.track(
    new THREE.MeshStandardMaterial({ map: hairTexture(), roughness: 0.78, metalness: 0, alphaTest: 0.5, side: THREE.DoubleSide }),
  );
  const mesh = new THREE.SkinnedMesh(geo, mat);
  mesh.frustumCulled = false;
  mesh.castShadow = false;
  tail.add(mesh);
  tail.updateMatrixWorld(true);
  mesh.bind(new THREE.Skeleton(bones));

  const tmp = new THREE.Vector3();
  // The strands' own thickness: the chain rides this far above the bodywork.
  const clear = 0.11 * s;
  return {
    group,
    update: (ctx, dt): void => {
      const h = Math.min(dt, 1 / 30);
      const ax = -ctx.lateralSpeed * 1.6;
      const az = -ctx.speed * 0.55 - ctx.accel * 0.4;
      // Gravity, eased by the airflow at speed: hair streams nearly level
      // behind the head at race pace instead of dragging down the tail.
      const ay = -9 + Math.min(6, ctx.speed * 0.13);
      for (let i = 1; i < N; i++) {
        const p = pts[i]!;
        const q = prev[i]!;
        tmp.copy(p).sub(q).multiplyScalar(0.88); // velocity, damped
        q.copy(p);
        p.add(tmp);
        p.x += ax * h * h;
        p.y += ay * h * h;
        p.z += az * h * h;
      }
      for (let it = 0; it < 3; it++) {
        for (let i = 1; i < N; i++) {
          const p = pts[i]!;
          const q = pts[i - 1]!;
          tmp.copy(p).sub(q);
          const d = tmp.length() || 1e-6;
          const corr = (d - seg) / d;
          if (i === 1) p.addScaledVector(tmp, -corr);
          else {
            p.addScaledVector(tmp, -corr * 0.5);
            q.addScaledVector(tmp, corr * 0.5);
          }
        }
        for (let i = 1; i < N; i++) {
          const p = pts[i]!;
          const top = restTopAt(a, opts.surface, p.x, p.z, true);
          const cape = ctx.capeTopAt?.(p.x, p.z) ?? null;
          const floor = Math.max(top ?? -Infinity, cape ?? -Infinity);
          if (floor > -Infinity && p.y < floor + clear) p.y = floor + clear;
        }
      }
      for (let i = 0; i < N; i++) {
        const b = bones[i]!;
        b.position.copy(pts[i]!);
        const next = i < N - 1 ? pts[i + 1]! : pts[i]!;
        const from = i < N - 1 ? pts[i]! : pts[i - 1]!;
        tmp.copy(next).sub(from);
        if (tmp.lengthSq() > 1e-8) b.quaternion.setFromUnitVectors(Z, tmp.normalize());
      }
    },
  };
};

// ---------------------------------------------------------------------------
// Fan Tifo — worn like a CAPE: a square of cloth fixed across the back of the
// roof (behind the cockpit on the open muscle car, the tailgate top on the
// pickup and the monster truck, the rider's or the pillion's shoulders on the
// bike, the airbox on the Formula) carrying the owner's design. At the grid it
// lies over the bodywork and hangs down the tail (capeShape: the TAUT profile
// of the ride's measured shape, so wings, fins, louvres, a faceted tailgate
// and bumpers stay under the cloth); as the car gains speed the wind lifts it
// and it streams and flaps behind, still clear of the body.
// ---------------------------------------------------------------------------

/** Where the cape hangs on a ride and how big it is (pure: the part builder
 *  and the tests share it). `mods` = the whole equipped list (the bike's cape
 *  moves to the pillion's shoulders when Fans Cheering ride along). */
export function tifoSpec(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[]): CapeSpec {
  const base = tifoBase(a, surf, mods);
  if (!mods.includes('mod.goalwing') || a.kind !== 'car') return base;
  // M154 r2: a Goal Wing that stands where it would hide the design stands on
  // the cape's rail instead (goalFrameFor), so nothing of it is between the
  // race camera and the cloth; where it stands clear (the pickup's and the
  // monster truck's goals over the bed), the design keeps out of what little
  // its net and crossbar cover.
  // (M154 polish r2: the open muscle car's goal always stands at its tail;
  // the design lies under its net, whose cords it is seen through, clear of
  // the crossbar's shadow)
  return goalFrameFor(a, surf, mods).rail ? base : { ...base, covers: goalwingCovers(a, surf, !netOverDeck(a)) };
}

/** M154 polish r3: a ride whose cape lies along a long deck under the Goal
 *  Wing's net (the open muscle car, the monster truck's flat deck): the
 *  design is seen through the net's fine mesh, so only the crossbar and its
 *  wing plane hide it (round 2 put the monster truck's goal on the cape's
 *  rail for that: a short wing squeezed between the cage's bars). */
export function netOverDeck(a: ModAnchors): boolean {
  return a.openTop || flatBed(a);
}

/** M154 r2: the share of the cloth the design must keep in plain sight for
 *  the Goal Wing to stand where it stands alone. Below it the goal moves onto
 *  the cape's rail: laid in what the goal left of the cloth, the design
 *  shrank to a third to a half of its size on the coupe, the sports car and
 *  the muscle car (in-game review). */
export const GOAL_KEEP_BAND = 0.9;

/** The Goal Wing's frame: the span between its posts, their height, the
 *  height their feet stand at and the z the goal stands at. `rail` = it stands
 *  on the cape's rail (the posts rise from the rail's two clasps, there are
 *  no feet and no net: the cape hangs back from the goal like its net). */
export interface GoalFrame {
  span: number;
  h: number;
  y: number;
  z: number;
  rail: boolean;
}

/**
 * M154 r2: where the Goal Wing stands (pure: the goal, the cape and the tests
 * share it). Where it always stands (goalwingFrame), unless the Fan Tifo is
 * worn too and the goal there would hide more than a tenth of the cape's
 * design from the race camera: then it stands ON THE CAPE'S RAIL, its posts
 * rising from the rail's clasps. From the chase camera everything at or
 * forward of the rail is seen above the cloth, so the design keeps its full
 * size, no crossbar or wing plane crosses it and the end plates stand clear
 * of its edges (round 1 laid the design in the stretch the goal left: a third
 * to a half of its size under a coloured bar, the end plates on the hem).
 */
export function goalFrameFor(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[]): GoalFrame {
  const stand = { ...goalwingFrame(a, surf), rail: false };
  // (M154 polish r2 kept the open muscle car's goal off the rail, at its
  // tail: right behind the seats its crossbar read as a dark roll bar. Polish
  // r4 puts it back on the rail, as live and as the coupe's and sports car's:
  // at the tail its crossbar hid the cape's front third from the race camera
  // and the design shrank to 57% of the live build's width.)
  if (a.kind !== 'car' || !mods.includes('mod.tifo')) return stand;
  // (M154 polish r4: never on the monster truck's rail: its cape hangs from
  // the cab ROOF's rear edge, and the goal stood up there on the roof, high
  // over the cab, its end plates two black rods against the sky and no net
  // from the race camera. It stands over the deck as live, net and all; the
  // design lies past its crossbar, seen through the net.)
  if (flatBed(a)) return stand;
  const base = tifoBase(a, surf, mods);
  const band = capeShape(surf, { ...base, covers: goalwingCovers(a, surf, !netOverDeck(a)) }, (x, z) => restTopAt(a, null, x, z, true)).band;
  if (band[1] - band[0] >= GOAL_KEEP_BAND) return stand;
  return { span: 2 * base.railHalf, h: stand.h, y: base.anchor.y + 0.025 * a.s, z: base.anchor.z, rail: true };
}

function tifoBase(a: ModAnchors, surf: SurfaceField | null, mods: readonly string[]): CapeSpec {
  const s = a.s;
  // Stand-off between cloth and body (the envelope already covers ridges).
  const clear = 0.03 + 0.015 * s;
  const seatAt = (x0: number, x1: number, z: number, fallback: number): number =>
    surf?.topIn(x0, x1, z - 0.04 * a.k, z + 0.04 * a.k) ?? fallback;
  let anchor: THREE.Vector3;
  let W: number;
  let Lmax: number;
  let railHalf: number;
  let hangFloor = Math.max(0.12, a.sillY);
  let designW: number | undefined;
  if (a.kind === 'bike') {
    // The rider's shoulders — or the PILLION fan's when Fans Cheering ride
    // along (the passenger sits exactly where the rider's cape would hang).
    // M153: the M128c point (the helmet's back, dropped) sat INSIDE the
    // tucked rider; the rail now rides just above his back, behind the helmet.
    const pillion = mods.includes('mod.fans') ? pillionSeat(a, surf) : null;
    if (pillion) {
      anchor = pillion.base.clone().add(V(0, 0.37 * pillion.H, -0.09 * pillion.H));
      W = 0.3 * pillion.H;
      railHalf = 0.12 * pillion.H;
    } else {
      const hz = (a.helmetBack ?? a.roofRear).z - 0.2 * a.k;
      anchor = V(0, seatAt(-0.12, 0.12, hz, (a.helmetBack ?? a.roofRear).y) + clear, hz);
      // A little wider than his shoulders, so it drapes over his back and the
      // tail's flanks and the design has room to read; M154 polish r3: never
      // wider than the tail it lies on (the round-4 tail is slimmer and the
      // cloth hung past its sides from the race camera).
      W = Math.min(0.95 * s, 2 * (bikeTailHalf(a, surf) + 0.03 * a.k));
      railHalf = Math.min(0.17 * a.k, W / 2);
      // (M154 polish r4: the design keeps the live cape's size on the
      // narrower cloth: from the race camera it had shrunk to about 80%)
      if (W < 0.95 * s) designW = 0.95 * s;
    }
    Lmax = anchor.z - a.tailZ + 0.4 * a.k;
    // Never down into the rear tyre: the hang stops above it.
    hangFloor = a.wheelY + a.wheelR + 0.05 * a.k;
  } else if (a.kind === 'f1') {
    // Off the top of the airbox behind the helmet (hung from the helmet it
    // lay INSIDE the engine cover, measured).
    const hz = (a.helmetBack?.z ?? a.roofRear.z) - 0.12 * s;
    anchor = V(0, seatAt(-0.1, 0.1, hz, a.deckY) + clear, hz);
    W = 0.9 * s;
    railHalf = 0.12 * a.k;
    Lmax = 1.2 * s;
  } else if (a.bed) {
    // A pickup wears it off the TAILGATE: hung from the top edge of the gate
    // (the fans keep their bed), flying flat behind.
    W = Math.min(a.bed.halfW * 2 + 0.1 * s, 1.75 * s);
    // M154 polish r2: a FLAT bed (the monster truck's: its deck, no tub)
    // carries it LYING on the deck from just behind the crew's row, over the
    // tail edge: hung off the gate it could only drop to above the tail's
    // lamp bar (a strip) or hang in front of it (the race camera lost the
    // lamps behind a flat board).
    let z = a.bed.z0 + 0.02 * s;
    railHalf = W / 2;
    Lmax = 1.5 * s;
    let roofRail = false;
    if (flatBed(a)) {
      const deck = flatDeckCape(a, surf, mods);
      z = deck.z;
      W = Math.min(W, deck.W);
      railHalf = Math.min(W / 2, deck.railHalf);
      roofRail = deck.roof;
    }
    // (M154 polish r3: hung from the cab roof's rear edge, the rail sits on
    // the roof's middle: measured across the cloth's whole width at a deck,
    // a cage bar's top raised it off the deck as a dark fin from the side)
    anchor = roofRail
      ? V(0, seatAt(-0.15, 0.15, z, a.roofRear.y) + clear, z)
      : V(0, seatAt(-W / 2, W / 2, z, a.deckY) + clear, z);
    if (roofRail) Lmax = 1.8 * s;
  } else if (a.openTop) {
    // The open muscle car: across the deck's front edge right behind the
    // seats, so the cape lies along the long deck and hangs down the tail
    // (M153: on the roofless body the roof-rear point was the deck's LIP).
    W = Math.min(a.halfW * 1.7, 1.75 * s);
    const z = a.roofFront.z - 0.02 * a.k;
    anchor = V(0, seatAt(-W / 2, W / 2, z, a.deckY) + clear, z);
    railHalf = W / 2 - 0.04 * a.k;
    Lmax = Math.min(2.5 * s, anchor.z - a.tailZ + 0.75 * s);
  } else {
    W = Math.min(a.halfW * 1.7, 1.75 * s);
    if (a.wing) {
      // M154: at the stock wing the cloth is at least as wide as the wing's
      // end plates: narrower, the plates stood out at both sides of the cape.
      // (M154 r2: no longer the Goal Wing's too: with the cape worn it stands
      // on the cape's rail, and the cloth widened to its plates put them on
      // the hem's edges.)
      const plates = a.wing.half + 0.012 * a.k;
      W = Math.max(W, 2 * (plates + 0.01 * a.k));
    }
    const z = a.roofRear.z + 0.04 * s;
    anchor = V(0, seatAt(-0.15, 0.15, z, a.roofRear.y) + clear, z);
    railHalf = Math.min(W / 2, roofHalfAt(surf, z, 0.08, a.halfW + 0.2, a.halfW * 0.7));
    Lmax = Math.min(2.5 * s, anchor.z - a.tailZ + 0.75 * s);
  }
  const floor0 = hangFloor;
  hangFloor = capeHem(a, surf, mods, anchor, W, clear, hangFloor);
  // M154 polish r2: a tailgate cape whose full width would have to stop above
  // lamps at the tail's corners (a pickup's C lamps) hangs BETWEEN them
  // instead, down the gate, when that gives the design more room.
  if (a.bed) {
    const n = capeBetweenLamps(a, surf, mods, anchor, W, clear, floor0, hangFloor);
    if (n) {
      W = n.W;
      railHalf = n.W / 2;
      hangFloor = n.hem;
    }
  }
  // M154 polish: a tailgate cape the whistles cut to a strip (the monster
  // truck's: they stand on the valance just under the gate, and the hem had
  // to stop above them) hangs BETWEEN them instead, down past them to the
  // bumper, when that gives the design more room.
  if (a.bed && !flatBed(a) && mods.includes('mod.whistle')) {
    const n = capeBetweenWhistles(a, surf, mods, anchor, W, clear, floor0, hangFloor);
    if (n) {
      W = n.W;
      railHalf = n.W / 2;
      hangFloor = n.hem;
    }
  }
  return {
    anchor: { x: anchor.x, y: anchor.y, z: anchor.z },
    W,
    Lmax,
    clear,
    hangFloor,
    railHalf,
    crossSlope: a.kind === 'bike' ? 0.6 : 0.9,
    covers: [],
    ...(designW !== undefined ? { designW } : {}),
  };
}

/** M154 polish r3 (pure): the half-width of a bike tail's top, where it
 *  stays within 6 cm of its height on the centre line (Infinity without a
 *  measured body). */
export function bikeTailHalf(a: ModAnchors, surf: SurfaceField | null): number {
  if (!surf) return Infinity;
  let half = Infinity;
  for (let z = a.tailZ + 0.08 * a.k; z <= a.tailZ + 0.45 * a.k; z += 0.03) {
    const c = surf.topAt(0, z);
    if (c === null) continue;
    let x = 0;
    while (x < 0.6 * a.k) {
      const t = surf.topAt(x + 0.01, z);
      if (t === null || c - t > 0.06) break;
      x += 0.01;
    }
    half = Math.min(half, x);
  }
  return half;
}

/**
 * M154: the lowest the cape's hem may hang. Never below where the tail's
 * face ends (a painted bumper's lower edge over a diffuser or an exhaust
 * surround, else the face's lowest row — the cloth hung on past it), and
 * above any worn part that stands in the hang's way across the cloth: the
 * whistle pipes (the hem bit into them) and the mud flaps (the cloth's corner
 * lay over a flap).
 */
export function capeHem(
  a: ModAnchors,
  surf: SurfaceField | null,
  mods: readonly string[],
  anchor: THREE.Vector3,
  W: number,
  clear: number,
  base: number,
): number {
  const x0 = anchor.x - W / 2;
  const x1 = anchor.x + W / 2;
  // HARD floors: what the cloth must never reach (the sills or the bike's
  // tyre, the worn parts it would cut). The SOFT floor: where the tail's face
  // ends, which a lamp may still push the hem a little below.
  let hard = base;
  let soft = base;
  if (surf) {
    // (judged over the middle of the cloth: the tail's corners are wings,
    // flares and lamp wraps that tell nothing about where the face ends)
    // M154 r2: a painted band over a dark one ends the face only over a
    // SHAPED DIFFUSER (the coupe, the sports car): the monster truck's
    // rebuilt tail (a painted lip over its lamp bar and plate valance) cut
    // its cape to a 19 cm strip.
    const bottom = surf.tailFaceBottom(anchor.x - 0.35 * W, anchor.x + 0.35 * W, anchor.y, base, 0.15, 0.09, 0.03, a.diffuser);
    if (bottom !== null) soft = Math.max(soft, bottom);
  }
  // the plane the cloth hangs in: just behind the tail across its band
  const hangZ = (surf?.rearZ(x0, x1, Math.max(hard, soft), anchor.y) ?? a.tailZ) - clear;
  const gap = 0.03 * a.k;
  if (mods.includes('mod.whistle')) {
    // M154 r2: the hem clears each whistle where it CROSSES the cloth's
    // plane (the body and bowl), not its whole box: the lanyard rings sit on
    // top of the bodies inside the tail and the mouthpieces stick out toward
    // the camera, and clearing those cut the bike's cape off mid-tail.
    for (const top of whistleCrossTops(a, surf, hangZ, x0, x1, undefined, mods)) hard = Math.max(hard, top + gap);
  }
  if (mods.includes('mod.mudflaps') && a.kind === 'car') {
    const f = mudflapLayout(a, surf);
    for (const sx of [-1, 1]) {
      const b = { x0: sx * f.x - f.w / 2, x1: sx * f.x + f.w / 2, z0: f.zb - 0.05 * a.s, z1: f.zb + 0.05 * a.s };
      if (b.x1 > x0 && b.x0 < x1 && b.z0 <= hangZ + 0.1 && b.z1 >= hangZ - 0.1) hard = Math.max(hard, f.top + 0.022 * a.s + gap);
    }
  }
  // The face keeps the cape a hang of its own (20 cm at least); a part the
  // cloth must clear does not (the bike's whistles sit under a tail end barely
  // lower than the rail: the hem rides over them).
  const top = anchor.y - 0.02 * a.k;
  let floor = Math.max(hard, Math.min(soft, anchor.y - 0.2 * a.k));
  // M154 polish r2: the hem never covers a tail lamp the race camera would
  // see: it ends ABOVE every lamp the cloth hangs over (round 2 let it end
  // below a lamp when nothing hard was there, and the coupe's LED blade, the
  // muscle car's and sports car's tail lamps, the monster truck's lamp bar
  // and the bike's LED V all disappeared behind the cloth from the chase
  // view). A bar of lamps joined by dark trim counts whole (lampMask).
  // (the hem hangs `clear` behind the tail face, and its folds a little
  // further: seen from above and behind it projects that much lower on the
  // face, so the gap over a lamp grows with it)
  // M154 polish r3: ...by how far the hem really hangs BEHIND each lamp:
  // the cloth hangs from the tail's rearmost point (a bumper lip, a
  // diffuser), often several centimetres behind a recessed lamp's face, and
  // the muscle car's hem sat on its lamps' top edges; and judged at the
  // steeper of the race camera's and the garage chase view's elevations.
  if (surf) {
    const runs = surf.coveredLampRuns(x0, x1, base, anchor.y);
    let depth = clear;
    for (const [l0, l1] of runs) {
      const lz = surf.rearZ(x0, x1, l0, l1);
      if (lz !== null) depth = Math.max(depth, lz - hangZ);
    }
    const lampGap = gap + (depth + CAPE_FOLD + 0.01) * Math.tan(Math.max(CHASE_ELEVATION, LAMP_VIEW_ELEVATION));
    floor = hemAboveLamps(runs, floor, lampGap, top);
  }
  return Math.min(floor, top);
}

/**
 * M154 polish r2 (pure; the tests pin it): when lamps the race camera sees
 * stand at a tailgate's corners, the narrower cape that hangs BETWEEN them,
 * clear of both by a hand, down the gate to the hem it then reaches
 * (capeHem); null when no lamp is under the full-width hang, a lamp (or a
 * lamp bar) runs across the middle, or the narrow cape's design (the smaller
 * of its width and its hang) would not be clearly bigger.
 */
export function capeBetweenLamps(
  a: ModAnchors,
  surf: SurfaceField | null,
  mods: readonly string[],
  anchor: THREE.Vector3,
  W: number,
  clear: number,
  base: number,
  hem: number,
): { W: number; hem: number } | null {
  if (!surf) return null;
  const half = surf.lampGapHalf(anchor.x, W / 2 + 0.05, base, anchor.y);
  if (half === null || !Number.isFinite(half)) return null;
  const Wn = 2 * (half - 0.03 * a.k);
  if (!(Wn > 0.35 * W) || Wn >= W) return null;
  const hemN = capeHem(a, surf, mods, anchor, Wn, clear, base);
  const design = (w: number, h: number): number => Math.min(w, anchor.y - h);
  return design(Wn, hemN) > 1.15 * design(W, hem) ? { W: Wn, hem: hemN } : null;
}

/** M154 polish r2: a bed whose floor IS the deck (the monster truck's flat
 *  deck the crew stands on), not a tub. */
export function flatBed(a: ModAnchors): boolean {
  return !!a.bed && a.bed.floorY >= a.deckY - 0.02 * a.k;
}

/**
 * M154 polish r2 (pure; the tests pin it): where the cape's rail crosses a
 * flat deck and how wide the cloth lying on it may be. As far forward as the
 * deck is clear (from the race camera a deck is foreshortened to about 0.4,
 * so the design's size follows the cloth's LENGTH: a short cloth read as a
 * strip with a small design): right behind the cab, as wide as the deck stays
 * flat all the way back (the roll cage's feet stand at the bed's front
 * corners); just behind the crew's boots when the fans ride there
 * (modProfile.fanLayout: their row stands CREW_ROW behind the cab).
 */
export function flatDeckCape(
  a: ModAnchors,
  surf: SurfaceField | null,
  mods: readonly string[] = [],
): { z: number; W: number; railHalf: number; roof: boolean } {
  const full = Math.min((a.bed?.halfW ?? a.halfW) * 2 + 0.1 * a.s, 1.75 * a.s);
  if (!a.bed) return { z: a.deckZ, W: full, railHalf: full / 2, roof: false };
  const crewZ = a.bed.z1 - (CREW_ROW + 0.24) * a.k;
  // (a cloth lying on a deck reads as a strip when it is far wider than it
  // is long: no wider than DECK_CAPE_ASPECT of its length)
  const crewW = Math.min(full, DECK_CAPE_ASPECT * (crewZ - a.tailZ + 0.1 * a.k));
  const crew = { z: crewZ, W: crewW, railHalf: crewW / 2, roof: false };
  if (mods.includes('mod.fans') || !surf) return crew;
  const z0 = a.bed.z1 - 0.14 * a.k;
  // the half-width over which the body stays level across (within 6 cm of
  // its height on the centre line) from z1 back to z2
  const flatHalf = (z1: number, z2: number, start: number): number => {
    let half = start;
    for (let z = z1; z >= z2; z -= 0.03) {
      const c = surf.topAt(0, z);
      if (c === null) continue;
      let x = 0;
      while (x + 0.02 <= half) {
        const t = surf.topAt(x + 0.02, z);
        if (t === null || Math.abs(t - c) > 0.06) break;
        x += 0.02;
      }
      half = Math.min(half, x);
    }
    return half;
  };
  const half = flatHalf(z0 + 0.04 * a.k, a.tailZ + 0.05, full / 2);
  // M154 polish r3: the cloth hangs from the cab ROOF's rear edge, like
  // every roofed car's, down the cab's back and along the deck to its tail
  // edge. Round 2 laid it on the deck alone from behind the cage: a small
  // sheet (owner review: "a dropped towel") whose rail stood up off the deck
  // as a fin. The tail's lamp bar starts right under the deck's edge, so the
  // cloth cannot hang down the tail; down the cab's back it shows the race
  // camera nearly square on and the design has room. It opens to the deck's
  // flat width once past the cab, its rail as wide as the cab's back is clear
  // between the cage's bars.
  const zr = a.roofRear.z + 0.04 * a.s;
  const W = Math.min(2 * (half - 0.04 * a.k), DECK_CAPE_ASPECT * (zr - a.tailZ + 0.1 * a.k));
  // (too narrow there: the crew's row, behind the cage)
  if (W < 0.5 * full) return crew;
  const back = flatHalf(zr - 0.02, z0, W / 2) - 0.04 * a.k;
  if (zr <= z0 || back < 0.25 * full) {
    const Wd = Math.min(W, DECK_CAPE_ASPECT * (z0 - a.tailZ + 0.1 * a.k));
    return { z: z0, W: Wd, railHalf: Wd / 2, roof: false };
  }
  return { z: zr, W, railHalf: Math.min(W / 2, back), roof: true };
}

/** M154 polish r2: the widest a cloth lying on a flat deck is drawn, as a
 *  share of its length. */
export const DECK_CAPE_ASPECT = 1.35;

/** M154 polish: a tailgate cape hanging less than this share of its width
 *  (once the whistles have set its hem) reads as a strip from the race
 *  camera (the monster truck's was about 1:3.5, its design a third of the
 *  coupe's). */
export const CAPE_STRIP = 0.4;

/**
 * M154 polish (pure; the tests pin it): when the whistles standing under a
 * tailgate cut its cape to a strip, the narrower cape that hangs BETWEEN
 * them, clear of both by a hand, to the hem it then reaches (capeHem: the
 * lamps' and flaps' rules still hold); null when the full-width cape does
 * not read as a strip, a whistle stands in the middle, or the narrow one's
 * design (the smaller of its width and its hang) would not be clearly
 * bigger.
 */
export function capeBetweenWhistles(
  a: ModAnchors,
  surf: SurfaceField | null,
  mods: readonly string[],
  anchor: THREE.Vector3,
  W: number,
  clear: number,
  base: number,
  hem: number,
): { W: number; hem: number } | null {
  if (anchor.y - hem >= CAPE_STRIP * W) return null;
  let inner = Infinity;
  for (const b of whistleBounds(a, surf, mods)) {
    if (b.x0 >= anchor.x) inner = Math.min(inner, b.x0 - anchor.x);
    else if (b.x1 <= anchor.x) inner = Math.min(inner, anchor.x - b.x1);
    else return null;
  }
  if (!Number.isFinite(inner)) return null;
  const Wn = 2 * (inner - 0.03 * a.k);
  if (!(Wn > 0.3 * W) || Wn >= W) return null;
  const hemN = capeHem(a, surf, mods, anchor, Wn, clear, base);
  const design = (w: number, h: number): number => Math.min(w, anchor.y - h);
  return design(Wn, hemN) > 1.15 * design(W, hem) ? { W: Wn, hem: hemN } : null;
}

/** M154 polish r2: the lowest hem that covers none of the lamp runs (pure;
 *  the tests pin it): raised `gap` above every lamp whose top is over it
 *  (the cloth would hang across or in front of it), never past `top`. */
export function hemAboveLamps(runs: readonly [number, number][], floor: number, gap: number, top: number): number {
  let y = floor;
  // (M154 polish r3: a lamp whose top is within the gap under the hem counts
  // too: the sports car's tail face ends AT its lamps' tops, and the hem sat
  // right on them)
  for (const [, l1] of [...runs].sort((p, q) => p[0] - q[0])) if (l1 > y - gap + 1e-6) y = Math.max(y, l1 + gap);
  return Math.min(y, top);
}

/** M154 polish r3: the garage and shop's chase view looks down at about 29
 *  degrees, steeper than the race camera: a hem judged clear of the lamps
 *  from there is clear in the race too. */
export const LAMP_VIEW_ELEVATION = 0.5;

/** How much TALLER the cape's design is drawn along the cloth than across it
 *  so it reads round from the race camera: none on a cape that hangs down a
 *  tail (the cars, the trucks), up to about double on one that mostly lies
 *  flat (the Formula's engine cover, the bike's tail) — M153: the Formula's
 *  smiley read as a squashed white shield. */
export function capeDesignStretch(chaseView: number): number {
  return Math.min(2, Math.max(1, (0.8 * Math.cos(CHASE_ELEVATION)) / Math.max(0.05, chaseView)));
}

export const tifo: PartFn = (a, c, m, opts) => {
  const s = a.s;
  const group = new THREE.Group();
  const spec = tifoSpec(a, opts.surface, opts.mods);
  const { W, railHalf } = spec;
  const anchor = V(spec.anchor.x, spec.anchor.y, spec.anchor.z);
  const shape = capeShape(opts.surface, spec, (x, z) => restTopAt(a, null, x, z, true));
  const L = shape.L;
  // The shoulder bar the cape hangs from: a slim rail with two kit-colour
  // clasps, as wide as the roof under it.
  const pb = new PartBuilder();
  pb.box(railHalf * 2 + 0.06 * s, 0.035 * s, 0.05 * s, anchor.x, anchor.y - 0.01 * s, anchor.z, DARK, { radius: 0.012 * s });
  for (const sx of [-1, 1]) {
    pb.box(0.09 * s, 0.05 * s, 0.09 * s, anchor.x + sx * railHalf, anchor.y, anchor.z, c.primary, { radius: 0.014 * s, bucket: 'paint' });
  }
  group.add(pb.build(m));
  const [b0, b1] = shape.band;
  const mat = m.track(
    new THREE.MeshStandardMaterial({
      map: m.trackTex(
        capeTexture(c.primary, c.secondary, opts.tifoStyle, b1 - b0, W / L, capeDesignStretch(shape.chaseView), b0, (spec.designW ?? W) / W),
      ),
      side: THREE.DoubleSide,
      roughness: 0.82,
    }),
  );
  const cloth = clothMesh(L, W, 28, 14, mat, { drape: shape.drape, fly: shape.fly });
  // Cloth frame: its length (x) streams back (-Z), its width (y) runs across
  // the car (+X), so its normal (z) is world DOWN — that is the gravity axis.
  // The flight sag is baked into `fly` (clamped clear of the body), so the
  // shader's own droop stays off.
  cloth.grav.set(0, 0, 1);
  cloth.mesh.matrixAutoUpdate = false;
  cloth.mesh.matrix.makeBasis(V(0, 0, -1), V(1, 0, 0), V(0, -1, 0)).setPosition(V(anchor.x + W / 2, anchor.y, anchor.z));
  group.add(cloth.mesh);
  let lift = 0;
  // The tail band the resting cloth covers: its rail down to its lowest hem.
  let hem = anchor.y;
  for (const v of [0, 0.25, 0.5, 0.75, 1]) hem = Math.min(hem, anchor.y - shape.drape(1, v)[2]);
  const tailBand = { x0: anchor.x - W / 2, x1: anchor.x + W / 2, y0: hem, y1: anchor.y, lift: 0 };
  // The cloth's current height over a car-local point on the ride (the part
  // that lies on / flies over the bodywork): the ponytail's floor, so hair
  // rides on the cape rather than through it. Mirrors the shader's blend.
  const surface = (x: number, z: number): number | null => {
    const rest = shape.restAt(x, z);
    if (rest === null) return null;
    const fly = shape.flyAt(x, z);
    return fly === null ? rest : rest + (fly - rest) * lift;
  };
  return {
    group,
    update: (ctx, dt): void => {
      const wind = windOf(ctx);
      lift = damp(lift, Math.min(1, wind * 1.35), 3, dt);
      cloth.lift.value = lift;
      cloth.droop.value = 0;
      // Flying it FLAPS (a fast travelling wave down the cloth plus a slower
      // cross ripple); parked: still.
      cloth.amp.value = (0.02 + 0.17 * wind) * s;
      cloth.time.value += dt * (0.6 + 3.2 * wind);
      ctx.capeTopAt = surface;
      tailBand.lift = lift;
      ctx.capeTail = tailBand;
    },
  };
};

// ---------------------------------------------------------------------------
// Goal Wing — a GT wing on goalposts, the wing plane in the kit colour, a net
// slung forward onto the deck that billows with speed. M154 r2: with the cape
// worn too it may stand on the cape's rail (goalFrameFor), the posts rising
// from the rail's clasps and the cape hanging back from it as its net.
// ---------------------------------------------------------------------------
export const goalwing: PartFn = (a, c, m, opts) => {
  const group = new THREE.Group();
  if (a.kind !== 'car') return { group };
  const s = a.s;
  const pb = new PartBuilder();
  const { span, h, y, z, rail } = goalFrameFor(a, opts.surface, opts.mods);
  for (const sx of [-1, 1]) {
    pb.cyl(0.038 * s, 0.038 * s, h, (sx * span) / 2, y + h / 2, z, WHITE);
    // (on the rail the clasp is the post's foot)
    if (!rail) pb.box(0.18 * s, 0.04 * s, 0.18 * s, (sx * span) / 2, y + 0.02 * s, z, DARK, { radius: 0.01 });
    pb.box(0.03 * s, 0.26 * s, 0.44 * s, sx * (span / 2 + 0.08 * s), y + h + 0.07 * s, z, DARK, { radius: 0.008 });
    pb.box(0.034 * s, 0.03 * s, 0.44 * s, sx * (span / 2 + 0.08 * s), y + h + 0.16 * s, z, WHITE);
  }
  pb.cyl(0.038 * s, 0.038 * s, span, 0, y + h, z, WHITE, { rot: [0, 0, Math.PI / 2] });
  pb.box(span + 0.14 * s, 0.055 * s, 0.38 * s, 0, y + h + 0.05 * s, z, c.primary, { rot: [R(9), 0, 0], radius: 0.014 * s, bucket: 'paint' });
  group.add(pb.build(m));
  // M154 polish r3: the live build's NET is back: a fine mesh slung forward
  // from the crossbar onto the measured body (round 2's white cord net read
  // as scaffolding, a picket rail and a cage over the roof number). On the
  // cape's rail there is none, as live: the cape hangs back from the goal
  // like its net and the roof number reads between the posts.
  const sheet = goalNetSheet(a, opts.surface, opts.mods);
  if (!sheet) return { group };
  const drop = sheet.foot.clone().sub(sheet.bar);
  const L = drop.length();
  const tex = m.trackTex(goalNetTexture().clone());
  tex.repeat.set(span / GOAL_NET_TILE, L / GOAL_NET_TILE);
  tex.needsUpdate = true;
  // (BLENDED, not cut at 0.3 as live: in the distance the mipmaps average a
  // mesh's alpha under any cut and the threads broke into stray hairs; blended
  // it fades to the even veil a real net shows at that distance. It writes no
  // depth, so the deck or the bed under it reads through it.)
  const mat = m.track(
    new THREE.MeshStandardMaterial({ map: tex, transparent: true, alphaTest: 0.01, depthWrite: false, side: THREE.DoubleSide, roughness: 0.85 }),
  );
  const net = clothMesh(L, span, 12, 16, mat);
  const d = drop.clone().normalize();
  const X = V(1, 0, 0);
  net.mesh.matrixAutoUpdate = false;
  net.mesh.matrix.makeBasis(d, X, d.clone().cross(X)).setPosition(V(span / 2, sheet.bar.y, sheet.bar.z));
  group.add(net.mesh);
  return {
    group,
    update: (ctx, dt): void => {
      const wind = windOf(ctx);
      net.amp.value = 0.015 * s + 0.045 * s * wind;
      net.time.value += dt * (0.6 + 2 * wind);
    },
  };
};

/** M154 polish r3: the goal net's texture tile (metres; eight meshes to a
 *  tile) and how far forward of the goal line it reaches (units of s): the
 *  live build's numbers. */
export const GOAL_NET_TILE = 0.4;
export const GOAL_NET_DEPTH = 0.58;

/**
 * M154 polish r3 (pure; the part and the tests share it): the Goal Wing's
 * net as the live build slung it, from the crossbar (`bar`, the middle of
 * it) forward to its foot on the measured body (`foot`), or null when there
 * is no net (a bike or a Formula; a goal on the cape's rail, whose cloth is
 * its net). Its foot rides just over the body under it and never lower than
 * a hand under the crossbar. With the Kit Number worn it ends short of the
 * roof number, so from the race camera the number reads clear of it.
 */
export function goalNetSheet(
  a: ModAnchors,
  surf: SurfaceField | null,
  mods: readonly string[],
): { bar: THREE.Vector3; foot: THREE.Vector3 } | null {
  if (a.kind !== 'car') return null;
  const s = a.s;
  const { h, y, z, rail } = goalFrameFor(a, surf, mods);
  // (nor over a cape lying along a deck under the goal, the open muscle
  // car's: seen from behind, the net's threads drew a comb over the design;
  // M154 polish r4: its goal stands on the cape's rail again. The monster
  // truck's net is back over its deck, as live.)
  if (rail || (mods.includes('mod.tifo') && a.openTop)) return null;
  const top = y + h;
  let depth = GOAL_NET_DEPTH * s;
  if (mods.includes('mod.kitnumber')) {
    // the roof number lies 0.6 s along the car (roofNumberZ)
    const front = roofNumberZ(a, surf, mods, 0.6 * s) - 0.3 * s - 0.05 * a.k;
    if (front > z) depth = Math.max(0.22 * s, Math.min(depth, front - z));
  }
  // The net never lies flatter than GOAL_NET_MIN_SLOPE: on the cars with a
  // low deck under the goal (the coupe, the sports car, the muscle car) the
  // live net fell about 30 degrees, the race camera's and the garage's own
  // elevation, so both saw it edge on: nothing, or a few stray white hairs
  // where its threads caught the light. Where the body is that close under
  // it, it reaches the body sooner (a steeper net, seen through the goal's
  // mouth); the pickup's net over its bed is as live.
  const footAt = (d: number): number => Math.min(top - 0.12 * s, (restTopAt(a, surf, 0, z + d) ?? y) + 0.03 * s);
  const minD = 0.15 * s;
  while (depth > minD && (top - footAt(depth)) / depth < GOAL_NET_MIN_SLOPE) depth = Math.max(minD, depth - 0.01);
  return { bar: V(0, top, z), foot: V(0, footAt(depth), z + depth) };
}

/** M154 polish r3: the flattest the Goal Wing's net may fall (rise over run:
 *  about 50 degrees), so the race camera never sees it edge on. */
export const GOAL_NET_MIN_SLOPE = 1.2;

interface Fan {
  group: THREE.Group;
  arms: THREE.Group;
  head: THREE.Mesh;
  scarf: ClothHandle;
  phase: number;
  baseY: number;
}

// A supporter: proportioned like a person (shoulders wider than the waist, a
// neck, an oval head with a face, ears, hair or a beanie), a kit shirt with
// short sleeves, white cuffs and a secondary-colour chest band, skin
// forearms with thumbed hands, white shorts, socks in the second colour and
// dark boots where legs show. `mode` sets the pose: standing, sat deep in a
// truck bed (nothing below the chest shows), sat on an edge with the legs
// dangling, sat on a flat deck with the knees up, leaning out of a window
// (one arm up, one on the door), or riding pillion. `H` is the standing
// height.
function buildFan(H: number, c: ModColors, m: ModMaterials, mode: FanMode, idx: number, side: number): Fan {
  const group = new THREE.Group();
  const skin = SKINS[idx % 4] ?? 0xd9a181;
  const hairC = HAIRS[(idx * 3 + 1) % 4] ?? 0x17120e;
  const beanie = idx % 2 === 0;
  const lean = { stand: 0, tub: R(14), edge: R(6), window: R(34), pillion: R(6), deck: R(8) }[mode];
  const hipY = { stand: 0.5 * H, tub: 0.02 * H, edge: 0, window: -0.2 * H, pillion: 0, deck: 0.04 * H }[mode];
  // A rotation about +X by +lean tips the top toward +Z, the figure's FACING
  // direction. Seated fans lean BACK (negative); a window fan faces out of
  // the door and must lean OUT of it (positive) — M128b leaned them into the
  // cabin (owner: "leaning into the car instead of out of the window").
  const leanA = mode === 'window' ? lean : -lean;
  const rotL = new THREE.Quaternion().setFromAxisAngle(V(1, 0, 0), leanA);
  const T = (x: number, y: number, z: number): THREE.Vector3 => V(x, y, z).applyQuaternion(rotL).add(V(0, hipY, 0));
  const eul: [number, number, number] = [leanA, 0, 0];
  const pb = new PartBuilder();
  const legs = mode === 'stand' || mode === 'edge' || mode === 'pillion' || mode === 'deck';
  if (legs) {
    for (const sx of [-1, 1]) {
      const hip = V(sx * 0.075 * H, hipY, 0);
      let knee: THREE.Vector3;
      let foot: THREE.Vector3;
      if (mode === 'stand') {
        knee = hip.clone().add(V(0, -0.25 * H, 0.01 * H));
        foot = knee.clone().add(V(0, -0.24 * H, 0));
      } else if (mode === 'edge') {
        knee = hip.clone().add(V(0, -0.1 * H, 0.22 * H));
        foot = knee.clone().add(V(0, -0.24 * H, 0.01 * H));
      } else if (mode === 'deck') {
        // M154 polish: sat on a flat deck, knees drawn up, boots planted on
        // it (the boot tip reaches DECK_REACH of the height ahead of the hip)
        knee = hip.clone().add(V(sx * 0.01 * H, 0.11 * H, 0.17 * H));
        foot = knee.clone().add(V(0, -0.125 * H, 0.08 * H));
      } else {
        // Pillion: knees out and feet on the pegs OUTBOARD of the bike's
        // frame (M128b hung them inside it, down onto the rear tyre — owner).
        // M153: the thighs run out level over the raised tail's edge before
        // the shins drop (angled down from the hip they came out of the
        // cowl's flanks, detached from the body).
        knee = hip.clone().add(V(sx * 0.14 * H, -0.005 * H, 0.11 * H));
        foot = knee.clone().add(V(-sx * 0.015 * H, -0.25 * H, -0.03 * H));
      }
      // Shorts (white), knee, sock (second colour), boot (dark).
      pb.rod(hip, knee, 0.052 * H, WHITE);
      pb.sphere(0.048 * H, knee.x, knee.y, knee.z, skin);
      const sockTop = knee.clone().lerp(foot, 0.35);
      pb.rod(knee, sockTop, 0.038 * H, skin);
      pb.rod(sockTop, foot, 0.04 * H, c.secondary, 'paint');
      pb.box(0.075 * H, 0.05 * H, 0.14 * H, foot.x, foot.y - 0.01 * H, foot.z + 0.03 * H, RUBBER, { radius: 0.012 * H });
    }
  }
  // Torso: waist, chest (broader), a chest band, collar, neck.
  const wa = T(0, 0.1 * H, 0);
  pb.box(0.2 * H, 0.14 * H, 0.12 * H, wa.x, wa.y, wa.z, c.primary, { rot: eul, radius: 0.03 * H, bucket: 'paint' });
  const ch = T(0, 0.26 * H, 0);
  pb.box(0.28 * H, 0.22 * H, 0.15 * H, ch.x, ch.y, ch.z, c.primary, { rot: eul, radius: 0.045 * H, bucket: 'paint' });
  const band = T(0, 0.26 * H, 0.076 * H);
  pb.box(0.07 * H, 0.2 * H, 0.012 * H, band.x, band.y, band.z, c.secondary, { rot: eul, radius: 0.004 * H, bucket: 'paint' });
  const bandBack = T(0, 0.26 * H, -0.076 * H);
  pb.box(0.07 * H, 0.2 * H, 0.012 * H, bandBack.x, bandBack.y, bandBack.z, c.secondary, { rot: eul, radius: 0.004 * H, bucket: 'paint' });
  for (const sx of [-1, 1]) {
    // Shoulder caps = the short sleeves' tops.
    const sh = T(sx * 0.15 * H, 0.35 * H, 0);
    pb.sphere(0.055 * H, sh.x, sh.y, sh.z, c.primary, { bucket: 'paint' });
  }
  const co = T(0, 0.39 * H, 0.005 * H);
  pb.torus(0.048 * H, 0.011 * H, co.x, co.y, co.z, WHITE, { rot: [Math.PI / 2 + leanA, 0, 0] });
  const ne = T(0, 0.425 * H, 0.008 * H);
  pb.cyl(0.032 * H, 0.036 * H, 0.07 * H, ne.x, ne.y, ne.z, skin, { rot: eul });
  const hc = T(0, 0.515 * H, 0.012 * H);
  const no = hc.clone().add(V(0, -0.006 * H, 0.064 * H).applyQuaternion(rotL));
  pb.sphere(0.014 * H, no.x, no.y, no.z, skin);
  for (const sx of [-1, 1]) {
    const ear = hc.clone().add(V(sx * 0.062 * H, -0.004 * H, 0).applyQuaternion(rotL));
    pb.sphere(0.014 * H, ear.x, ear.y, ear.z, skin, { scale: [0.6, 1, 1] });
  }
  if (beanie) {
    const be = hc.clone().add(V(0, 0.028 * H, -0.004 * H).applyQuaternion(rotL));
    pb.sphere(0.067 * H, be.x, be.y, be.z, c.primary, { scale: [1, 0.78, 1], bucket: 'paint' });
    const rim = hc.clone().add(V(0, 0.008 * H, -0.004 * H).applyQuaternion(rotL));
    pb.torus(0.062 * H, 0.014 * H, rim.x, rim.y, rim.z, WHITE, { rot: [Math.PI / 2 + leanA, 0, 0] });
    const po = hc.clone().add(V(0, 0.092 * H, -0.006 * H).applyQuaternion(rotL));
    pb.sphere(0.024 * H, po.x, po.y, po.z, WHITE);
  } else {
    const ha = hc.clone().add(V(0, 0.022 * H, -0.014 * H).applyQuaternion(rotL));
    pb.sphere(0.066 * H, ha.x, ha.y, ha.z, hairC, { scale: [1.02, 0.72, 1.06] });
    const fr = hc.clone().add(V(0, 0.04 * H, 0.05 * H).applyQuaternion(rotL));
    pb.box(0.09 * H, 0.03 * H, 0.04 * H, fr.x, fr.y, fr.z, hairC, { rot: eul, radius: 0.012 * H });
  }
  group.add(pb.build(m));
  // The head is its own mesh so it can wear a FACE (skin colour x the face
  // map) and turn; a SphereGeometry puts the map's centre on +X, so yaw it
  // to +Z. Slightly taller than wide so it reads as a head, not a ball.
  const headMat = m.track(new THREE.MeshStandardMaterial({ color: skin, map: faceTexture(), roughness: 0.7, metalness: 0 }));
  const head = new THREE.Mesh(new THREE.SphereGeometry(0.062 * H, 18, 14), headMat);
  head.position.copy(hc);
  head.scale.set(1, 1.1, 1);
  head.quaternion.copy(rotL).multiply(new THREE.Quaternion().setFromAxisAngle(V(0, 1, 0), -Math.PI / 2));
  head.castShadow = false;
  group.add(head);

  // Arms: one pivot at the shoulders; both arms raised with bent elbows,
  // hands holding the scarf overhead. A window fan raises the arm nearest
  // the tail and rests the other on the door.
  const arms = new THREE.Group();
  arms.position.copy(T(0, 0.35 * H, 0.005 * H));
  arms.quaternion.copy(rotL);
  const ab = new PartBuilder();
  const hands: THREE.Vector3[] = [];
  const arm = (sx: number, up: THREE.Vector3, fa: THREE.Vector3): THREE.Vector3 => {
    const shoulder = V(sx * 0.15 * H, 0, 0);
    const el = shoulder.clone().addScaledVector(up, 0.2 * H);
    const sleeveEnd = shoulder.clone().addScaledVector(up, 0.09 * H);
    const hd = el.clone().addScaledVector(fa, 0.19 * H);
    ab.rod(shoulder, sleeveEnd, 0.04 * H, c.primary, 'paint');
    ab.torus(0.036 * H, 0.008 * H, sleeveEnd.x, sleeveEnd.y, sleeveEnd.z, WHITE, {
      rot: new THREE.Quaternion().setFromUnitVectors(V(0, 0, 1), up.clone().normalize()),
    });
    ab.rod(sleeveEnd, el, 0.031 * H, skin);
    ab.sphere(0.033 * H, el.x, el.y, el.z, skin);
    ab.rod(el, hd, 0.028 * H, skin);
    ab.sphere(0.036 * H, hd.x, hd.y, hd.z, skin, { scale: [1, 1.15, 0.8] });
    const th = hd.clone().addScaledVector(V(-sx, 0, 0.4).normalize(), 0.03 * H);
    ab.sphere(0.017 * H, th.x, th.y, th.z, skin);
    return hd;
  };
  const armSides = mode === 'window' ? [side] : [-1, 1];
  for (const sx of armSides) {
    const ang = R(34);
    const up = V(sx * Math.sin(ang), Math.cos(ang), 0.05).normalize();
    const fa = V(sx * Math.sin(ang - R(38)), Math.cos(ang - R(38)), 0.12).normalize();
    hands.push(arm(sx, up, fa));
  }
  if (mode === 'window') {
    const sx = -side;
    arm(sx, V(sx * 0.18, -1, 0.3).normalize(), V(sx * 0.15, -0.65, 0.75).normalize());
  }
  arms.add(ab.build(m));
  const scarfMat = m.track(new THREE.MeshStandardMaterial({ map: m.trackTex(scarfTexture(c.primary)), side: THREE.DoubleSide, roughness: 0.8 }));
  let scarf: ClothHandle;
  if (hands.length === 2 && hands[0] && hands[1]) {
    const A = hands[0];
    const B = hands[1];
    scarf = clothMesh(A.distanceTo(B), 0.16 * H, 12, 3, scarfMat);
    scarf.mesh.position.copy(A);
    scarf.mesh.quaternion.setFromUnitVectors(V(1, 0, 0), B.clone().sub(A).normalize());
  } else {
    const A = hands[0] ?? V(0, 0.3 * H, 0);
    scarf = clothMesh(0.42 * H, 0.15 * H, 12, 3, scarfMat);
    scarf.mesh.position.copy(A);
    scarf.mesh.rotation.y = side > 0 ? -Math.PI / 2 : Math.PI / 2; // streams toward the car's tail
  }
  arms.add(scarf.mesh);
  group.add(arms);
  return { group, arms, head, scarf, phase: idx * 1.7, baseY: 0 };
}

export const fans: PartFn = (a, c, m, opts) => {
  const group = new THREE.Group();
  const list: Fan[] = [];
  // (the places are shared: the corner flags keep clear of them, M154 r2)
  for (const f of fanLayout(a, opts.surface, opts.mods)) {
    const fan = buildFan(f.H, c, m, f.mode, f.idx, f.side);
    fan.group.position.copy(f.p);
    fan.group.rotation.y = f.yaw;
    fan.baseY = f.p.y;
    list.push(fan);
    group.add(fan.group);
  }
  return {
    group,
    update: (ctx, dt): void => {
      const excite = 0.3 + 0.2 * windOf(ctx);
      for (const f of list) {
        const w = ctx.t * 6.5 + f.phase;
        f.arms.rotation.x = -0.25 - excite * Math.sin(w);
        f.group.position.y = f.baseY + 0.03 * Math.max(0, Math.sin(w));
        f.group.rotation.z = 0.06 * Math.sin(w * 0.5 + f.phase);
        f.head.rotation.y = -Math.PI / 2 + 0.35 * Math.sin(ctx.t * 0.9 + f.phase * 2.1);
        f.scarf.amp.value = 0.02 + 0.02 * windOf(ctx);
        f.scarf.time.value += dt * 2.2;
      }
    },
  };
};
